import { useEffect, useState, type ReactNode } from 'react';
import { isAxiosError } from 'axios';
import type { User, RoleComputePresetDTO, MonitoringArea } from '../../../types/auth';
import {
  formatCount,
  formatMoney,
  readStoredCurrency,
  writeStoredCurrency,
  displayMonthlyUsdForCadence,
  type BillingCadenceDisplay,
  type ProfileCurrency,
} from '../../../config/billingProfile';
import { coerceRoleValue, PLANTATION_SUB_ROLE_OPTIONS } from '../../../config/audience';
import { presetForRole } from '../../../config/adminComputePresets';
import { PlanCardShell, PlanMetricRow } from '../../../components/PlanCardShell';
import authService from '../../../services/authService';
import {
  enrichMonitoringAreaLabels,
  fetchLguMonitoringArea,
} from '../../../services/lguMonitoringArea';
import { requiresPlanPurchase, introTrialDaysLeft, introTrialResetOnUtcLabel, introTrialSpanDays } from '../../../utils/billingTrial';
import type { PayMongoIntentResponse } from '../../../types/billing';
import { paymongoPublishableKey } from '../../../config/paymongoPublic';
import billingService from '../../../services/billingService';
import { paymongoCompleteCardPayment } from '../../../services/paymongoBrowser';
import ConfirmPlanCheckout from './ConfirmPlanCheckout';

/** Profile / plan edits from this modal are not persisted until re-enabled. */
const PROFILE_MODAL_SAVE_DISABLED = true;

/** Checkout copy for repeat subscribers (PCI-safe: brand + last4 only — no vaulted id in client). */
function savedVaultedPaymentLabel(user: User): string {
  const brand = user.payment_method_card_brand?.trim();
  const four = user.payment_method_last4?.trim();
  if (brand?.length && four?.length) {
    return `${brand.charAt(0).toUpperCase()}${brand.slice(1).toLowerCase()} ····${four}`;
  }
  if (four?.length) return `Card ····${four}`;
  if (brand?.length) return `${brand.charAt(0).toUpperCase()}${brand.slice(1).toLowerCase()} on file`;
  return 'Saved PayMongo payment method';
}

const BADGE_POPULAR =
  'pointer-events-none whitespace-nowrap rounded-full bg-emerald-600 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-md shadow-emerald-900/25';

const CURRENT_PLAN_FOOTER_CLASS =
  'mt-auto flex min-h-11 w-full items-center justify-center rounded-lg border border-slate-200 bg-slate-50 px-2 py-2.5 text-center text-[11px] font-semibold leading-snug text-slate-800 shadow-sm ring-1 ring-slate-100 sm:text-xs';

function PlanConcurrentLoginNote({
  variant,
  accent,
}: {
  variant: 'single' | 'quad';
  accent: 'teal' | 'blue' | 'orange';
}) {
  const [open, setOpen] = useState(false);
  const shell = {
    teal: 'border-teal-200/90 bg-teal-50/80 ring-teal-100/55',
    blue: 'border-blue-200/90 bg-blue-50/80 ring-blue-100/55',
    orange: 'border-orange-200/90 bg-orange-50/80 ring-orange-100/55',
  }[accent];

  const title = variant === 'single' ? 'One login at a time' : 'Up to four logins active';
  const detail =
    variant === 'single'
      ? 'If another person logs in with the same account elsewhere, older sessions sign out automatically.'
      : 'Same account can stay signed in on four browsers or devices; one session does not sign out the others.';

  return (
    <div className={`w-full overflow-hidden rounded-xl border text-left shadow-sm ring-1 ${shell}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition hover:bg-black/[0.03]"
        aria-expanded={open}
      >
        <span className="min-w-0 flex-1 text-[11px] font-semibold leading-snug text-slate-900">{title}</span>
        <i
          className={`ri-arrow-down-s-line shrink-0 text-base text-slate-500 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
      </button>
      {open ? (
        <div className="border-t border-slate-900/[0.06] px-3 pb-2.5 pt-1.5">
          <p className="text-[10px] leading-snug text-slate-600">{detail}</p>
        </div>
      ) : null}
    </div>
  );
}

const PROFILE_ROLES: { value: string; label: string }[] = [
  { value: 'blo', label: 'BLO (big land owner)' },
  { value: 'plantation_owner', label: 'Plantation owner' },
  { value: 'lgu', label: 'LGU' },
  { value: 'project_developer', label: 'Carbon Project Developer' },
];

type LimitMode = 'daily' | 'monthly' | 'unlimited';

function computeLimitMarketingLabel(mode: string | undefined | null): string {
  const m = (mode || 'daily').toLowerCase();
  if (m === 'monthly') return 'Monthly Sulit';
  if (m === 'unlimited') return 'Unlimited Access';
  return 'Daily Access';
}

function activePlanFromUser(user: User): LimitMode | null {
  if (user.is_system_guest || user.is_superuser) return null;
  const m = (user.compute_limit_mode || 'daily').toLowerCase();
  if (m === 'monthly') return 'monthly';
  if (m === 'unlimited') return 'unlimited';
  return 'daily';
}

function planTierRank(mode: LimitMode): number {
  switch (mode) {
    case 'daily':
      return 0;
    case 'monthly':
      return 1;
    case 'unlimited':
      return 2;
    default:
      return 0;
  }
}

/** Solid CTA when upgrading or first-time choose (no active tier in UI). */
function planUpgradeCtaLabel(cardMode: LimitMode): string {
  switch (cardMode) {
    case 'daily':
      return 'Upgrade to Daily Access';
    case 'monthly':
      return 'Upgrade to Monthly Sulit';
    case 'unlimited':
      return 'Upgrade to Unlimited Access';
    default:
      return 'Choose plan';
  }
}

/** Outline CTA when current plan is higher than this card. */
function planSwitchDownCtaLabel(cardMode: LimitMode): string {
  switch (cardMode) {
    case 'daily':
      return 'Switch to Daily Access';
    case 'monthly':
      return 'Switch to Monthly Sulit';
    case 'unlimited':
      return 'Switch to Unlimited Access';
    default:
      return 'Choose plan';
  }
}

/** No active tier (e.g. guest flow): neutral “choose” copy, not “upgrade”. */
function planChooseCtaLabel(cardMode: LimitMode): string {
  switch (cardMode) {
    case 'daily':
      return 'Choose Daily Access';
    case 'monthly':
      return 'Choose Monthly Sulit';
    case 'unlimited':
      return 'Choose Unlimited Access';
    default:
      return 'Choose plan';
  }
}

type PlanCtaVariant = 'solid' | 'outline';

function planCardCta(cardMode: LimitMode, activePlanMode: LimitMode | null): { label: string; variant: PlanCtaVariant } {
  if (activePlanMode == null) {
    return { label: planChooseCtaLabel(cardMode), variant: 'solid' };
  }
  if (planTierRank(cardMode) > planTierRank(activePlanMode)) {
    return { label: planUpgradeCtaLabel(cardMode), variant: 'solid' };
  }
  return { label: planSwitchDownCtaLabel(cardMode), variant: 'outline' };
}

function planCardFooterButtonClass(cardMode: LimitMode, variant: PlanCtaVariant): string {
  const base =
    'mt-auto flex min-h-11 w-full items-center justify-center rounded-lg px-2 py-2.5 text-center text-[10px] font-bold leading-snug shadow-sm transition sm:text-[11px] disabled:cursor-not-allowed disabled:opacity-45';
  if (variant === 'solid') {
    const solid = {
      daily: 'text-white bg-teal-600 hover:bg-teal-700',
      monthly: 'text-white bg-blue-600 hover:bg-blue-700',
      unlimited: 'text-white bg-orange-600 hover:bg-orange-700',
    }[cardMode];
    return `${base} ${solid}`;
  }
  const outline = {
    daily: 'border-2 border-teal-600 bg-white text-teal-800 hover:bg-teal-50',
    monthly: 'border-2 border-blue-600 bg-white text-blue-800 hover:bg-blue-50',
    unlimited: 'border-2 border-orange-600 bg-white text-orange-800 hover:bg-orange-50',
  }[cardMode];
  return `${base} ${outline}`;
}

function ActiveIntroTrialNote({
  cardMode,
  activePlanMode,
  user,
}: {
  cardMode: LimitMode;
  activePlanMode: LimitMode | null;
  user: User;
}) {
  const trialDaysLeft = introTrialDaysLeft(user);
  if (activePlanMode !== cardMode || trialDaysLeft == null) return null;
  const resetOn = introTrialResetOnUtcLabel(user);
  const spanDays = introTrialSpanDays(user);
  return (
    <p className="rounded-lg border border-emerald-200/90 bg-emerald-50/95 px-3 py-2 text-center text-[11px] font-medium leading-snug text-emerald-950">
      Resets on <span className="font-bold tabular-nums">{resetOn ?? '—'}</span>
      {spanDays != null ? (
        <>
          {' '}(<span className="font-semibold tabular-nums">{spanDays}</span>-day intro ·{' '}
          <span className="font-bold tabular-nums">{trialDaysLeft}</span> day{trialDaysLeft === 1 ? '' : 's'} left).
        </>
      ) : (
        <>
          {' '}(
          <span className="font-bold tabular-nums">{trialDaysLeft}</span> day{trialDaysLeft === 1 ? '' : 's'} left).
        </>
      )}
    </p>
  );
}

function QuotedMonthlyPlanPrice({
  listMonthlyUsd,
  cadence,
  currency,
}: {
  listMonthlyUsd: number;
  cadence: BillingCadenceDisplay;
  currency: ProfileCurrency;
}) {
  const monthlyEquiv = displayMonthlyUsdForCadence(listMonthlyUsd, cadence);
  const annual = cadence === 'annual';
  const headlineUsd = annual ? monthlyEquiv * 12 : monthlyEquiv;
  const listCompareUsd = annual ? listMonthlyUsd * 12 : listMonthlyUsd;
  return (
    <div className="space-y-1">
      <div>
        {annual ? (
          <p className="text-sm tabular-nums text-gray-400 line-through">
            {formatMoney(listCompareUsd, currency)} <span className="text-xs font-normal">/yr</span>
          </p>
        ) : null}
        <p className="text-2xl font-bold tracking-tight text-gray-900 sm:text-[1.65rem]" aria-live="polite">
          {formatMoney(headlineUsd, currency)}{' '}
          <span className="text-sm font-semibold text-gray-600">{annual ? '/yr' : '/mo'}</span>
        </p>
      </div>
      {annual ? (
        <p className="text-[11px] font-semibold leading-snug text-emerald-700">
          20% off vs monthly billing · ≈ {formatMoney(monthlyEquiv, currency)} /mo avg
        </p>
      ) : null}
    </div>
  );
}

function splitFullName(full: string | null | undefined): {
  first: string;
  last: string;
  middle: string;
} {
  if (!full?.trim()) return { first: '', last: '', middle: '' };
  const raw = full.trim();
  let suffixPart = '';
  const suffixMatch = raw.match(/\s+(JR\.?|SR\.?|II|III|IV|V)\s*$/i);
  let rest = raw;
  if (suffixMatch) {
    const s = suffixMatch[1].replace(/\.$/, '').toUpperCase();
    suffixPart = s === 'JR' ? 'Jr.' : s === 'SR' ? 'Sr.' : s;
    rest = raw.slice(0, -suffixMatch[0].length).trim();
  }
  const tokens = rest.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return { first: '', last: suffixPart, middle: '' };
  }
  if (tokens.length === 1) {
    return suffixPart
      ? { first: tokens[0], last: suffixPart, middle: '' }
      : { first: tokens[0], last: '', middle: '' };
  }
  let last = tokens[tokens.length - 1];
  if (suffixPart) last = `${last} ${suffixPart}`;
  return {
    first: tokens[0],
    last,
    middle: tokens.slice(1, -1).join(' '),
  };
}

function joinFullName(parts: { first: string; last: string; middle: string }): string {
  const m = parts.middle.trim();
  const bits = [parts.first.trim(), m, parts.last.trim()].filter(Boolean);
  return bits.join(' ').trim();
}

function formatRegisteredAt(iso: string): string {
  try {
    const d = new Date(iso);
    const s = new Intl.DateTimeFormat('en-PH', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: 'Asia/Manila',
    }).format(d);
    return `${s} PHT`;
  } catch {
    return '—';
  }
}

function formatPhpFromCentavos(centavos: number): string {
  return `₱${(centavos / 100).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function billingApiErrorMessage(e: unknown): string {
  if (isAxiosError(e)) {
    const raw = e.response?.data as { detail?: unknown } | undefined;
    const detail = raw?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((entry) => (typeof entry === 'object' && entry !== null ? JSON.stringify(entry) : String(entry)))
        .join(' ');
    }
    if (detail !== undefined && detail !== null) return String(detail);
    return e.message || 'Billing request failed.';
  }
  if (e instanceof Error) return e.message;
  return 'Could not start payment.';
}

interface UserProfileModalProps {
  open: boolean;
  user: User;
  onClose: () => void;
  onSaved: () => Promise<void>;
  /** `page` fills the area next to the side menu (no overlay). `modal` is the old centered pop-up. */
  variant?: 'modal' | 'page';
}

function ProfileField({
  label,
  requiredMark,
  children,
}: {
  label: string;
  requiredMark?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <label className="mb-1 block text-xs font-medium text-gray-600">
        {label}
        {requiredMark ? <span className="text-emerald-600">*</span> : null}
      </label>
      {children}
    </div>
  );
}

export default function UserProfileModal({ open, user, onClose, onSaved, variant = 'modal' }: UserProfileModalProps) {
  const isPage = variant === 'page';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const accountRole = (() => {
    const r = coerceRoleValue(user.role || 'blo');
    return ['blo', 'plantation_owner', 'lgu', 'project_developer'].includes(r) ? r : 'blo';
  })();
  const accountRoleLabel = (() => {
    if (user.is_superuser) return 'Administrator';
    const base = PROFILE_ROLES.find((item) => item.value === accountRole)?.label ?? accountRole;
    if (accountRole !== 'plantation_owner') return base;
    const sub = PLANTATION_SUB_ROLE_OPTIONS.find(
      (item) => item.value === (user.sub_role === 'coconut' ? 'coconut' : 'banana'),
    );
    return sub ? `${base} · ${sub.label}` : base;
  })();
  const [limitMode, setLimitMode] = useState<LimitMode>(
    ((user.compute_limit_mode || 'daily').toLowerCase() as LimitMode) || 'daily'
  );
  const [currency, setCurrency] = useState<ProfileCurrency>(() => readStoredCurrency());
  const [saving, setSaving] = useState(false);
  const [monthlyExtraHours, setMonthlyExtraHours] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [computePresets, setComputePresets] = useState<RoleComputePresetDTO[]>([]);
  const [monitoringAreaDisplay, setMonitoringAreaDisplay] = useState<MonitoringArea | null>(
    user.monitoring_area ?? null
  );
  const [monitoringAreaLoading, setMonitoringAreaLoading] = useState(false);
  const [monitoringAreaError, setMonitoringAreaError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<'account' | 'confirm'>('account');
  const [billingCadence, setBillingCadence] = useState<BillingCadenceDisplay>('monthly');
  const [paymongoLoading, setPaymongoLoading] = useState(false);
  const [vaultAttachConflictSignal, setVaultAttachConflictSignal] = useState(0);
  const [paymongoReady, setPaymongoReady] = useState<PayMongoIntentResponse | null>(null);
  const [paymongoAttachMessage, setPaymongoAttachMessage] = useState<string | null>(null);
  const [planActivatedBanner, setPlanActivatedBanner] = useState<string | null>(null);
  const [billingPrefLoading, setBillingPrefLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    void authService
      .getRoleComputePresets()
      .then(setComputePresets)
      .catch(() => setComputePresets([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (String(user.role || '').toLowerCase() !== 'lgu') {
      setMonitoringAreaDisplay(null);
      setMonitoringAreaError(null);
      setMonitoringAreaLoading(false);
      return;
    }

    let cancelled = false;
    setMonitoringAreaLoading(true);
    setMonitoringAreaError(null);
    setMonitoringAreaDisplay(user.monitoring_area ?? null);

    void (async () => {
      try {
        let area: MonitoringArea | null = null;
        try {
          area = await fetchLguMonitoringArea();
        } catch (err: unknown) {
          const ax = err as { response?: { status?: number; data?: { detail?: unknown } } };
          if (ax.response?.status === 403) {
            if (!cancelled) {
              setMonitoringAreaDisplay(null);
              setMonitoringAreaError(null);
            }
            return;
          }
          // Fall back to /auth/me payload if the dedicated call fails.
          area = user.monitoring_area ?? null;
          if (!area) {
            const detail = ax.response?.data?.detail;
            throw new Error(typeof detail === 'string' ? detail : 'Could not load monitoring area.');
          }
        }
        if (!area) {
          if (!cancelled) setMonitoringAreaDisplay(null);
          return;
        }
        const enriched = await enrichMonitoringAreaLabels(area);
        if (!cancelled) setMonitoringAreaDisplay(enriched);
      } catch (e: unknown) {
        if (!cancelled) {
          setMonitoringAreaError(e instanceof Error ? e.message : 'Could not load monitoring area.');
        }
      } finally {
        if (!cancelled) setMonitoringAreaLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, user.id, user.role, user.monitoring_area]);

  /** Reset checkout billing UI when modal opens/closes (not on every presets/user refresh — that was clearing confirm/PayMongo). */
  useEffect(() => {
    if (open) {
      setCheckoutStep('account');
      setPaymongoReady(null);
      setPaymongoAttachMessage(null);
      setPlanActivatedBanner(null);
      setError(null);
      setIsEditing(false);
      setVaultAttachConflictSignal(0);
      return;
    }
    setCheckoutStep('account');
    setPaymongoReady(null);
    setPaymongoAttachMessage(null);
    setPlanActivatedBanner(null);
  }, [open]);

  /** Hydrate profile + plan selectors from props; skip while user is on the PayMongo confirm screen. */
  useEffect(() => {
    if (!open) return;
    if (checkoutStep === 'confirm') return;

    const parts = splitFullName(user.full_name);
    setFirstName(parts.first);
    setLastName(parts.last);
    setMiddleName(parts.middle);
    setLimitMode(((user.compute_limit_mode || 'daily').toLowerCase() as LimitMode) || 'daily');
    const m = (user.compute_limit_mode || '').toLowerCase();
    const prInit = presetForRole(computePresets, user.role);
    const packMs = prInit.monthly_pack_hours * 3_600_000;
    if (m === 'monthly' && user.compute_budget_ms_per_month != null) {
      const raw = Math.round((user.compute_budget_ms_per_month - packMs) / 3_600_000);
      setMonthlyExtraHours(Math.max(0, Math.min(prInit.max_monthly_extra_hours, raw)));
    } else {
      setMonthlyExtraHours(0);
    }
    setCurrency(readStoredCurrency());
  }, [open, checkoutStep, user, computePresets]);

  useEffect(() => {
    if (limitMode !== 'monthly') return;
    const pr = presetForRole(computePresets, user.role);
    setMonthlyExtraHours((h) => Math.min(h, pr.max_monthly_extra_hours));
  }, [limitMode, user.role, computePresets]);

  const pr = presetForRole(computePresets, user.role);
  const totalMonthlyHours = pr.monthly_pack_hours + Math.max(0, monthlyExtraHours);
  const extraHoursCostUsd = Math.max(0, monthlyExtraHours) * pr.price_extra_per_hour_usd;
  const activePlanMode = activePlanFromUser(user);
  const dailyPlanFooterCta = activePlanMode === 'daily' ? null : planCardCta('daily', activePlanMode);
  const monthlyPlanFooterCta = activePlanMode === 'monthly' ? null : planCardCta('monthly', activePlanMode);
  const unlimitedPlanFooterCta = activePlanMode === 'unlimited' ? null : planCardCta('unlimited', activePlanMode);
  const paymongoPublicKey = paymongoPublishableKey();

  if (!open) return null;

  const canEditPlan = !user.is_system_guest && !!user.email;
  const canSaveDisplayCurrency = user.is_system_guest === true;
  const planClickable = canEditPlan && !user.is_superuser;
  const canEditProfileFields = canEditPlan;
  const profileFieldsLocked = !isEditing || !canEditProfileFields;

  const handleSave = async () => {
    if (PROFILE_MODAL_SAVE_DISABLED) return;
    setSaving(true);
    setError(null);
    try {
      writeStoredCurrency(currency);
      if (canSaveDisplayCurrency) {
        onClose();
        return;
      }
      if (!canEditPlan) return;
      const full = joinFullName({ first: firstName, last: lastName, middle: middleName });
      if (user.is_superuser) {
        await authService.updateProfile({
          full_name: full || undefined,
        });
        await onSaved();
        onClose();
        return;
      }
      await authService.updateProfile({
        full_name: full || undefined,
        compute_limit_mode: limitMode,
        ...(limitMode === 'monthly' ? { monthly_extra_hours: monthlyExtraHours } : {}),
      });
      await onSaved();
      onClose();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : null;
      setError(typeof msg === 'string' ? msg : 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  const inputBase =
    'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-emerald-500/40 focus:outline-none focus:ring-2 focus:ring-emerald-500/15 disabled:cursor-not-allowed';
  const readOnlyBg = profileFieldsLocked || !canEditPlan ? 'bg-gray-50 text-gray-700' : 'bg-white';

  const choosePlan = (mode: LimitMode) => {
    if (!planClickable) return;
    setLimitMode(mode);
    setPaymongoReady(null);
    setPaymongoAttachMessage(null);
    setPlanActivatedBanner(null);
    setCheckoutStep('confirm');
  };

  return (
    <div
      className={
        isPage
          ? 'fixed inset-0 z-[90] overflow-y-auto bg-gray-100 transition-[left] duration-200 ease-out motion-reduce:transition-none lg:left-[var(--nav-sidebar-w)] lg:top-[68px]'
          : 'fixed inset-0 z-[200] flex items-center justify-center bg-black/45 p-3 backdrop-blur-sm sm:p-4'
      }
      role={isPage ? 'region' : 'dialog'}
      aria-modal={isPage ? undefined : true}
      aria-labelledby={checkoutStep === 'confirm' ? 'confirm-plan-title' : 'profile-my-account-title'}
      onClick={
        isPage
          ? undefined
          : (e) => {
              if (e.target === e.currentTarget) onClose();
            }
      }
    >
      <div
        className={
          isPage
            ? `relative mx-auto w-full ${checkoutStep === 'confirm' ? 'max-w-6xl' : 'max-w-5xl'}`
            : `relative max-h-[92vh] w-full overflow-y-auto rounded-2xl bg-gray-100 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_24px_60px_-12px_rgba(0,0,0,0.2)] ring-1 ring-black/[0.05] ${
                checkoutStep === 'confirm' ? 'max-w-6xl' : 'max-w-5xl'
              }`
        }
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-20 rounded-xl p-2 text-gray-400 transition hover:bg-gray-200/90 hover:text-gray-700"
          aria-label="Close"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="px-5 pb-6 pt-8 sm:px-8 sm:pb-8 sm:pt-10">
          {checkoutStep === 'confirm' ? (
            <>
              {paymongoReady ? (
                <div className="mb-4 space-y-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-[13px] text-emerald-950 shadow-sm sm:px-5">
                  <p className="font-semibold text-emerald-950">PayMongo payment intent is ready</p>
                  <p>
                    Charge amount:{' '}
                    <span className="font-bold tabular-nums text-emerald-950">{formatPhpFromCentavos(paymongoReady.amount)}</span>
                    <span className="text-emerald-800/90"> ({paymongoReady.currency})</span>
                  </p>
                  <p className="text-[12px] text-emerald-900/85">Checkout method: credit or debit card (PayMongo).</p>
                  {paymongoAttachMessage ? (
                    <p className="rounded-lg border border-emerald-300/80 bg-white/80 px-3 py-2 text-sm font-medium text-emerald-950">
                      {paymongoAttachMessage}
                    </p>
                  ) : null}
                  {!paymongoPublicKey ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-950">
                      <p className="font-semibold text-amber-950">Publishable key missing in the Web app build</p>
                      <p className="mt-1">
                        Add <span className="font-mono font-semibold">VITE_PAYMONGO_PUBLIC_KEY=pk_test_...</span> to{' '}
                        <span className="font-mono">WebGIS.../.env</span>, then stop and run{' '}
                        <span className="font-mono">npm run dev</span> again (or <span className="font-mono">npm run build</span>{' '}
                        for production). Vite only injects <span className="font-mono">VITE_*</span> variables at startup / build
                        time.
                      </p>
                    </div>
                  ) : (
                    <p className="text-[11px] leading-snug text-emerald-900/85">
                      {user.billing_has_saved_paymongo_payment_method
                        ? 'If you subscribed before, choose “Pay with saved card” in checkout — otherwise enter card details.'
                        : 'Card details are entered on this step. If you need to try again, use Start over and re-enter your card.'}
                    </p>
                  )}
                  <details className="rounded-lg border border-emerald-200/80 bg-white/50 text-[11px] text-emerald-900">
                    <summary className="cursor-pointer select-none px-3 py-2 font-medium text-emerald-900">
                      Developer: client key (for debugging)
                    </summary>
                    <div className="border-t border-emerald-100 px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <code className="max-w-full break-all rounded bg-white px-2 py-1.5 leading-snug text-slate-800 ring-1 ring-emerald-200/90">
                          {paymongoReady.client_key}
                        </code>
                        <button
                          type="button"
                          onClick={() => void navigator.clipboard.writeText(paymongoReady.client_key)}
                          className="rounded-lg border border-emerald-700/30 bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-800"
                        >
                          Copy client key
                        </button>
                      </div>
                    </div>
                  </details>
                  <button
                    type="button"
                    onClick={() => {
                      setPaymongoReady(null);
                      setPaymongoAttachMessage(null);
                    }}
                    className="text-sm font-semibold text-emerald-800 underline underline-offset-2 hover:text-emerald-950"
                  >
                    Start over
                  </button>
                </div>
              ) : null}
              {error ? (
                <p className="mb-4 px-2 text-center text-sm text-red-600" role="alert">
                  {error}
                </p>
              ) : null}
              <ConfirmPlanCheckout
                limitMode={limitMode}
                pr={pr}
                currency={currency}
                billingCadence={billingCadence}
                monthlyExtraHours={monthlyExtraHours}
                onMonthlyExtraHoursChange={(n) =>
                  setMonthlyExtraHours(Math.max(0, Math.min(pr.max_monthly_extra_hours, Math.floor(n))))
                }
                totalMonthlyHours={totalMonthlyHours}
                extraHoursCostUsd={extraHoursCostUsd}
                formatMoney={formatMoney}
                onChangePlan={() => {
                  setPaymongoReady(null);
                  setPaymongoAttachMessage(null);
                  setPlanActivatedBanner(null);
                  setCheckoutStep('account');
                }}
                onBack={() => {
                  setPaymongoReady(null);
                  setPaymongoAttachMessage(null);
                  setPlanActivatedBanner(null);
                  setCheckoutStep('account');
                }}
                defaultBillingEmail={user.email}
                proceedLoading={paymongoLoading}
                vaultAttachConflictSignal={vaultAttachConflictSignal}
                savedPaymentMethodEligible={Boolean(user.billing_has_saved_paymongo_payment_method)}
                savedPaymentMethodLabel={savedVaultedPaymentLabel(user)}
                onProceedWithSavedPayment={async ({ billing_auto_renew }) => {
                  setPaymongoLoading(true);
                  setError(null);
                  setPaymongoReady(null);
                  setPaymongoAttachMessage(null);
                  try {
                    const res = await billingService.createPayMongoPaymentIntent({
                      limit_mode: limitMode,
                      billing_cadence: billingCadence,
                      monthly_extra_hours: limitMode === 'monthly' ? monthlyExtraHours : 0,
                      payment_panel: 'card',
                    });
                    setPaymongoReady(res);
                    const att = await billingService.attachPayMongoSavedPaymentMethod({
                      payment_intent_id: res.payment_intent_id,
                    });
                    if (att.redirect_url) {
                      window.location.assign(att.redirect_url);
                      return;
                    }
                    setPaymongoAttachMessage(att.message ?? null);
                    const ist = (att.intent_status || '').toLowerCase();
                    if (ist === 'succeeded') {
                      try {
                        const me = await billingService.confirmPayMongoSubscription({
                          payment_intent_id: res.payment_intent_id,
                          billing_auto_renew,
                        });
                        await onSaved();
                        const label = computeLimitMarketingLabel(me.compute_limit_mode);
                        setPaymongoAttachMessage(null);
                        setPaymongoReady(null);
                        setPlanActivatedBanner(
                          `${label} is active — your compute budget matches this plan.`,
                        );
                        setCheckoutStep('account');
                        setError(null);
                      } catch (confirmErr: unknown) {
                        setError(billingApiErrorMessage(confirmErr));
                      }
                    }
                  } catch (e: unknown) {
                    if (isAxiosError(e) && e.response?.status === 409) {
                      setVaultAttachConflictSignal((n) => n + 1);
                    }
                    setPaymongoReady(null);
                    setPaymongoAttachMessage(null);
                    setError(billingApiErrorMessage(e));
                  } finally {
                    setPaymongoLoading(false);
                  }
                }}
                onProceed={async ({ card, billing_auto_renew }) => {
                  setPaymongoLoading(true);
                  setError(null);
                  setPaymongoReady(null);
                  setPaymongoAttachMessage(null);
                  try {
                    const res = await billingService.createPayMongoPaymentIntent({
                      limit_mode: limitMode,
                      billing_cadence: billingCadence,
                      monthly_extra_hours: limitMode === 'monthly' ? monthlyExtraHours : 0,
                      payment_panel: 'card',
                    });
                    setPaymongoReady(res);

                    if (!paymongoPublicKey) {
                      setError(
                        'Add VITE_PAYMONGO_PUBLIC_KEY to your Web app .env so the browser can send the card to PayMongo.',
                      );
                      return;
                    }
                    try {
                      const outcome = await paymongoCompleteCardPayment(paymongoPublicKey, res, card);
                      if (outcome.status === 'redirect') {
                        window.location.assign(outcome.url);
                        return;
                      }
                      setPaymongoAttachMessage(outcome.message);

                      if (outcome.status === 'success') {
                        try {
                          const me = await billingService.confirmPayMongoSubscription({
                            payment_intent_id: res.payment_intent_id,
                            billing_auto_renew,
                            ...(outcome.payment_method_id
                              ? { payment_method_id: outcome.payment_method_id }
                              : {}),
                          });
                          await onSaved();
                          const label = computeLimitMarketingLabel(me.compute_limit_mode);
                          setPaymongoAttachMessage(null);
                          setPaymongoReady(null);
                          setPlanActivatedBanner(
                            `${label} is active — your compute budget matches this plan.`,
                          );
                          setCheckoutStep('account');
                          setError(null);
                        } catch (confirmErr: unknown) {
                          setError(billingApiErrorMessage(confirmErr));
                        }
                      }
                    } catch (attachErr: unknown) {
                      setPaymongoAttachMessage(null);
                      const raw = attachErr instanceof Error ? attachErr.message : 'PayMongo could not charge this card.';
                      let msg = raw;
                      if (/details\.card_number format is invalid|card_number format is invalid/i.test(raw)) {
                        msg =
                          'Card number was rejected. Use digits only (12–19), or use a PayMongo test card from their docs.';
                      }
                      setError(msg);
                    }
                  } catch (e: unknown) {
                    setPaymongoReady(null);
                    setPaymongoAttachMessage(null);
                    setError(billingApiErrorMessage(e));
                  } finally {
                    setPaymongoLoading(false);
                  }
                }}
              />
            </>
          ) : (
            <>
              <header className="mb-8 text-center">
                <h1 id="profile-my-account-title" className="text-2xl font-bold tracking-tight text-gray-900 sm:text-[1.75rem]">
                  My Account
                </h1>
                <p className="mt-1.5 text-sm text-gray-600">Manage your profile and plan details</p>
              </header>

              {planActivatedBanner ? (
                <div
                  className="mb-6 rounded-xl border border-emerald-200/90 bg-emerald-50 px-4 py-3 text-center text-sm font-medium leading-snug text-emerald-950 shadow-sm ring-1 ring-emerald-100/70"
                  role="status"
                >
                  <span className="font-semibold">{planActivatedBanner}</span>
                  <button
                    type="button"
                    className="ml-2 align-baseline text-xs font-semibold text-emerald-800 underline underline-offset-2 hover:text-emerald-950"
                    onClick={() => setPlanActivatedBanner(null)}
                  >
                    Dismiss
                  </button>
                </div>
              ) : null}

          {/* Profile */}
          <section className="rounded-2xl border border-gray-200/80 bg-white px-5 py-6 shadow-sm sm:px-7 sm:py-7">
            <h2 className="mb-5 text-center text-lg font-bold text-gray-900">Profile</h2>

            <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
              <ProfileField label="Email address" requiredMark>
                <input
                  type="email"
                  readOnly
                  value={user.email || ''}
                  className={`${inputBase} cursor-not-allowed bg-gray-50 text-gray-700`}
                />
              </ProfileField>
              <ProfileField label="Mobile number">
                <input
                  type="text"
                  readOnly
                  disabled
                  value=""
                  placeholder="—"
                  className={`${inputBase} cursor-not-allowed bg-gray-50 placeholder:text-gray-400`}
                  title="Mobile number is not stored on your profile yet."
                />
              </ProfileField>
              <ProfileField label="Date registered">
                <input
                  readOnly
                  value={formatRegisteredAt(user.created_at)}
                  className={`${inputBase} cursor-default bg-gray-50 text-gray-700`}
                />
              </ProfileField>
              <ProfileField label="Role">
                <input
                  readOnly
                  value={accountRoleLabel}
                  className={`${inputBase} cursor-default bg-gray-50 text-gray-700`}
                />
              </ProfileField>

              <ProfileField label="Last name" requiredMark>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={profileFieldsLocked}
                  className={`${inputBase} ${readOnlyBg}`}
                />
              </ProfileField>
              <ProfileField label="First name">
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={profileFieldsLocked}
                  className={`${inputBase} ${readOnlyBg}`}
                />
              </ProfileField>
              <ProfileField label="Middle name">
                <input
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                  disabled={profileFieldsLocked}
                  placeholder="(optional)"
                  className={`${inputBase} ${readOnlyBg}`}
                />
              </ProfileField>
            </div>

            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => {
                  if (!canEditProfileFields) return;
                  setIsEditing((v) => !v);
                }}
                disabled={!canEditProfileFields}
                className="rounded-lg border-2 border-emerald-600 bg-white px-8 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-white"
              >
                {isEditing ? 'Done' : 'Edit Profile'}
              </button>
            </div>
          </section>

          {String(user.role || '').toLowerCase() === 'lgu' ? (
            <section className="mt-6 rounded-2xl border border-gray-200/80 bg-white px-5 py-6 shadow-sm sm:px-7 sm:py-7">
              <h2 className="mb-2 text-center text-lg font-bold text-gray-900">Monitoring area</h2>
              <p className="mb-4 text-center text-xs text-gray-500">
                Assigned on Digisaka registration. Farms and farmers follow this area.
              </p>
              {monitoringAreaLoading ? (
                <p className="text-center text-sm text-gray-500">Loading monitoring area…</p>
              ) : monitoringAreaError ? (
                <p className="text-center text-sm text-red-600" role="alert">
                  {monitoringAreaError}
                </p>
              ) : monitoringAreaDisplay ? (
                <div className="mx-auto max-w-lg overflow-hidden rounded-[14px] border border-emerald-600/25 bg-emerald-50/80">
                  <div className="border-b border-emerald-600/15 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800/70">
                      Level
                    </p>
                    <p className="mt-0.5 text-base font-bold capitalize text-emerald-950">
                      {String(monitoringAreaDisplay.level || '—')}
                    </p>
                  </div>
                  <dl className="divide-y divide-emerald-600/10 text-sm">
                    {(
                      [
                        {
                          label: 'Province',
                          name: monitoringAreaDisplay.province,
                          id: monitoringAreaDisplay.province_id,
                          show: true,
                        },
                        {
                          label: 'Municipality / city',
                          name: monitoringAreaDisplay.municipality,
                          id: monitoringAreaDisplay.city_id,
                          show:
                            String(monitoringAreaDisplay.level || '').toLowerCase() !== 'province',
                        },
                        {
                          label: 'Barangay',
                          name: monitoringAreaDisplay.barangay,
                          id: monitoringAreaDisplay.barangay_id,
                          show:
                            String(monitoringAreaDisplay.level || '').toLowerCase() === 'barangay',
                        },
                      ] as const
                    )
                      .filter((row) => row.show)
                      .map((row) => (
                        <div key={row.label} className="flex items-start justify-between gap-3 px-4 py-2.5">
                          <dt className="shrink-0 text-emerald-900/70">{row.label}</dt>
                          <dd className="min-w-0 text-right font-medium text-emerald-950">
                            {row.name ? (
                              <span className="block truncate">{String(row.name)}</span>
                            ) : row.id != null && String(row.id).trim() !== '' ? (
                              <span className="block font-mono text-[12px] text-emerald-800/70">
                                {String(row.id)}
                              </span>
                            ) : (
                              <span className="text-emerald-800/50">—</span>
                            )}
                          </dd>
                        </div>
                      ))}
                  </dl>
                </div>
              ) : (
                <p className="text-center text-sm text-gray-600">
                  No monitoring area assigned on your Digisaka account yet. Farms and farmers stay
                  unavailable until an area is set.
                </p>
              )}
            </section>
          ) : null}

          {/* Pricing */}
          <section className="mt-6 rounded-2xl border border-gray-200/80 bg-white px-5 py-6 shadow-sm sm:px-7 sm:py-8">
            {requiresPlanPurchase(user) ? (
              <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-snug text-amber-950">
                Your free intro month on daily limits has ended. Switch to <strong>Monthly Sulit</strong> or{' '}
                <strong>Unlimited Access</strong> (or contact us) to continue with a paid analysis allowance.
              </div>
            ) : null}

            <h2 className="text-center text-lg font-bold text-gray-900">Pricing</h2>
            <p className="mt-2 px-2 text-center text-sm italic text-gray-600">
              {billingCadence === 'monthly'
                ? 'Reference prices at monthly billing. Cancel anytime.'
                : 'Annual billing applies 20% off the monthly reference rate (billed once yearly). Cancel anytime.'}
            </p>

            <div className="mt-5 flex flex-col items-center gap-2 border-b border-gray-100 pb-5 sm:flex-row sm:justify-center sm:gap-6">
              <span className="text-xs font-medium text-gray-600">Display currency (pricing reference)</span>
              <div className="flex flex-wrap justify-center gap-5">
                {(['PHP', 'USD'] as const).map((c) => (
                  <label key={c} className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="profile-currency"
                      checked={currency === c}
                      onChange={() => setCurrency(c)}
                      className="accent-emerald-600"
                    />
                    <span className="text-sm text-gray-800">{c}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-4 flex flex-col items-center gap-2 border-b border-gray-100 pb-5">
              <span className="text-xs font-medium text-gray-600">Billing cycle</span>
              <div className="flex flex-wrap justify-center gap-6">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-800">
                  <input
                    type="radio"
                    name="profile-billing-cadence"
                    checked={billingCadence === 'monthly'}
                    onChange={() => setBillingCadence('monthly')}
                    className="accent-emerald-600"
                  />
                  Monthly
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-800">
                  <input
                    type="radio"
                    name="profile-billing-cadence"
                    checked={billingCadence === 'annual'}
                    onChange={() => setBillingCadence('annual')}
                    className="accent-emerald-600"
                  />
                  Annual{' '}
                  <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800">
                    Save 20%
                  </span>
                </label>
              </div>
            </div>

            {planClickable &&
            ['monthly', 'unlimited'].includes((user.compute_limit_mode || '').toLowerCase()) ? (
              <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/90 px-4 py-3">
                {user.subscription_period_end ? (
                  <p className="mb-3 text-[13px] leading-snug text-slate-800">
                    <span className="font-semibold text-slate-900">Paid period ends (UTC): </span>
                    {(() => {
                      try {
                        const d = new Date(user.subscription_period_end);
                        if (Number.isNaN(d.getTime())) return String(user.subscription_period_end);
                        return d.toLocaleString('en-US', {
                          timeZone: 'UTC',
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        });
                      } catch {
                        return String(user.subscription_period_end);
                      }
                    })()}
                  </p>
                ) : (
                  <p className="mb-3 text-[12px] leading-snug text-slate-600">
                    After your next successful subscription payment, the paid-period end date appears here (UTC).
                  </p>
                )}
                <label className="flex cursor-pointer items-start gap-3 text-left">
                  <input
                    type="checkbox"
                    checked={user.billing_auto_renew !== false}
                    onChange={async (e) => {
                      if (!planClickable) return;
                      setBillingPrefLoading(true);
                      setError(null);
                      try {
                        await authService.updateProfile({ billing_auto_renew: e.target.checked });
                        await onSaved();
                      } catch (prefErr: unknown) {
                        setError(billingApiErrorMessage(prefErr));
                      } finally {
                        setBillingPrefLoading(false);
                      }
                    }}
                    disabled={billingPrefLoading}
                    className="mt-0.5 accent-emerald-600"
                  />
                  <span className="min-w-0 text-[13px] leading-snug text-slate-800">
                    <span className="font-semibold text-slate-900">Auto-renew</span> — we automatically try to charge
                    your saved card near the period end (PayMongo). Turn this off to renew manually.
                  </span>
                </label>
              </div>
            ) : null}

            {user.is_system_guest ? (
              <p className="mt-6 text-center text-sm text-amber-800">
                Guest accounts cannot change roles or analysis plans. Sign in for a full account.
              </p>
            ) : null}

            <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3 lg:gap-4">
              <PlanCardShell
                gradientClass="from-emerald-400 via-emerald-500 to-teal-500"
                className={
                  activePlanMode === 'daily'
                    ? 'outline outline-[3px] outline-offset-2 outline-teal-600 shadow-[0_8px_30px_-8px_rgba(13,148,136,0.35)]'
                    : ''
                }
                selected={limitMode === 'daily'}
                selectedRingClassName="ring-teal-500"
                minHeightClassName="min-h-0 lg:min-h-[280px]"
                innerClassName="gap-3 p-4 sm:gap-3 sm:p-5"
              >
                <span className="text-[11px] font-bold uppercase tracking-wider text-teal-600">Daily Access</span>
                <QuotedMonthlyPlanPrice
                  listMonthlyUsd={pr.price_base_usd}
                  cadence={billingCadence}
                  currency={currency}
                />
                <p className="text-xs italic text-gray-500">Per UTC day allowance</p>
                <PlanConcurrentLoginNote variant="single" accent="teal" />

                <ActiveIntroTrialNote
                  cardMode="daily"
                  activePlanMode={activePlanMode}
                  user={user}
                />

                <div className="mt-2 rounded-xl bg-gray-50/90 py-4 ring-1 ring-inset ring-gray-100">
                  <p className="text-center text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    Hours per UTC day
                  </p>
                  <div className="mt-2 flex flex-col items-center gap-1">
                    <span className="text-4xl font-bold leading-none text-teal-600">{formatCount(pr.daily_hours)}</span>
                    <span className="text-xs font-medium text-gray-500">hr/day</span>
                  </div>
                </div>

                {activePlanMode === 'daily' ? (
                  <p className={CURRENT_PLAN_FOOTER_CLASS} role="status">
                    Your current plan
                  </p>
                ) : dailyPlanFooterCta ? (
                  <button
                    type="button"
                    disabled={!planClickable}
                    className={planCardFooterButtonClass('daily', dailyPlanFooterCta.variant)}
                    onClick={(e) => {
                      e.stopPropagation();
                      choosePlan('daily');
                    }}
                  >
                    {dailyPlanFooterCta.label}
                  </button>
                ) : null}
              </PlanCardShell>

              <PlanCardShell
                gradientClass="from-sky-400 via-blue-500 to-indigo-500"
                badge={<span className={BADGE_POPULAR}>Most Popular</span>}
                className={
                  activePlanMode === 'monthly'
                    ? 'outline outline-[3px] outline-offset-2 outline-blue-600 shadow-[0_8px_30px_-8px_rgba(37,99,235,0.3)]'
                    : ''
                }
                selected={limitMode === 'monthly'}
                selectedRingClassName="ring-blue-500"
                minHeightClassName="min-h-0 lg:min-h-[280px]"
                innerClassName="gap-3 p-4 sm:gap-3 sm:p-5"
              >
                <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600">Monthly Sulit</span>
                <QuotedMonthlyPlanPrice
                  listMonthlyUsd={pr.price_pack_usd}
                  cadence={billingCadence}
                  currency={currency}
                />
                <p className="text-xs italic text-gray-500">Included hours + add-ons</p>
                <PlanConcurrentLoginNote variant="single" accent="blue" />

                <ActiveIntroTrialNote
                  cardMode="monthly"
                  activePlanMode={activePlanMode}
                  user={user}
                />

                <div className="space-y-2">
                  <PlanMetricRow
                    label="Included hours"
                    suffix="hrs"
                    middleClassName="text-blue-600 font-semibold tabular-nums"
                  >
                    <span>{formatCount(pr.monthly_pack_hours)}</span>
                  </PlanMetricRow>
                  <PlanMetricRow
                    label="Max add-on hours"
                    suffix="hrs"
                    middleClassName="text-blue-600 font-semibold tabular-nums"
                  >
                    <span>{formatCount(pr.max_monthly_extra_hours)}</span>
                  </PlanMetricRow>
                  <PlanMetricRow
                    label="Extra hour rate"
                    suffix="/ hr"
                    middleClassName="text-blue-600 font-semibold tabular-nums"
                  >
                    <span>{formatMoney(pr.price_extra_per_hour_usd, currency)}</span>
                  </PlanMetricRow>
                </div>

                {activePlanMode === 'monthly' ? (
                  <p className={CURRENT_PLAN_FOOTER_CLASS} role="status">
                    Your current plan
                  </p>
                ) : monthlyPlanFooterCta ? (
                  <button
                    type="button"
                    disabled={!planClickable}
                    className={planCardFooterButtonClass('monthly', monthlyPlanFooterCta.variant)}
                    onClick={(e) => {
                      e.stopPropagation();
                      choosePlan('monthly');
                    }}
                  >
                    {monthlyPlanFooterCta.label}
                  </button>
                ) : null}
              </PlanCardShell>

              <PlanCardShell
                gradientClass="from-amber-400 via-amber-500 to-orange-500"
                className={
                  activePlanMode === 'unlimited'
                    ? 'outline outline-[3px] outline-offset-2 outline-orange-600 shadow-[0_8px_30px_-8px_rgba(234,88,12,0.32)]'
                    : ''
                }
                selected={limitMode === 'unlimited'}
                selectedRingClassName="ring-orange-500"
                minHeightClassName="min-h-0 lg:min-h-[280px]"
                innerClassName="gap-3 p-4 sm:gap-3 sm:p-5"
              >
                <span className="text-[11px] font-bold uppercase tracking-wider text-orange-600">Unlimited Access</span>
                <QuotedMonthlyPlanPrice
                  listMonthlyUsd={pr.price_unlimited_usd}
                  cadence={billingCadence}
                  currency={currency}
                />
                <p className="text-xs italic text-gray-500">No cap. Max performance.</p>
                <PlanConcurrentLoginNote variant="quad" accent="orange" />

                <ActiveIntroTrialNote
                  cardMode="unlimited"
                  activePlanMode={activePlanMode}
                  user={user}
                />

                <PlanMetricRow label="Monthly hour cap" suffix="" middleClassName="text-orange-700 font-semibold">
                  <span className="text-[11px] font-bold uppercase tracking-tight sm:text-xs">Unlimited</span>
                </PlanMetricRow>

                {activePlanMode === 'unlimited' ? (
                  <p className={CURRENT_PLAN_FOOTER_CLASS} role="status">
                    Your current plan
                  </p>
                ) : unlimitedPlanFooterCta ? (
                  <button
                    type="button"
                    disabled={!planClickable}
                    className={planCardFooterButtonClass('unlimited', unlimitedPlanFooterCta.variant)}
                    onClick={(e) => {
                      e.stopPropagation();
                      choosePlan('unlimited');
                    }}
                  >
                    {unlimitedPlanFooterCta.label}
                  </button>
                ) : null}
              </PlanCardShell>
            </div>
          </section>

          {error ? (
            <p className="mt-5 px-2 text-center text-sm text-red-600" role="alert">
              {error}
            </p>
          ) : null}

          <div className="mt-5 flex flex-wrap justify-center gap-2 border-gray-100 sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={
                PROFILE_MODAL_SAVE_DISABLED || saving || (!canEditPlan && !canSaveDisplayCurrency)
              }
              className="rounded-lg bg-emerald-600 px-7 py-2.5 text-sm font-semibold text-white shadow-md shadow-emerald-600/25 transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-45"
            >
              {PROFILE_MODAL_SAVE_DISABLED
                ? 'Save unavailable'
                : saving
                  ? 'Saving…'
                  : canSaveDisplayCurrency
                    ? 'Save display'
                    : 'Save changes'}
            </button>
          </div>

          {user.is_superuser ? (
            <p className="mt-4 text-center text-[11px] text-gray-500">
              Administrator: compute limits can be adjusted in admin tools; update your display name via Save when saving
              is enabled.
            </p>
          ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
